import Button from '@experience/shared/components/Button';
import PasswordInputField from '@experience/shared/components/InputFields/PasswordInputField';
import { useState, useContext, useMemo, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingContext from '@ac/Providers/LoadingContextProvider/LoadingContext';
import PageContext from '@ac/Providers/PageContextProvider/PageContext';
import { verifyPassword } from '@ac/apis/verification';
import useApi from '@ac/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@ac/hooks/use-error-handler';
import SecondaryPageLayout from '@ac/layouts/SecondaryPageLayout';

import PasswordIdentifierInput from '../PasswordIdentifierInput';
import SwitchVerificationMethodLink from '../SwitchVerificationMethodLink';

import styles from './index.module.scss';

type Props = {
  readonly onBack?: () => void;
  readonly onSwitchMethod?: () => void;
  readonly hasAlternativeMethod?: boolean;
};

const PasswordVerification = ({ onBack, onSwitchMethod, hasAlternativeMethod }: Props) => {
  const { t } = useTranslation();
  const { setVerificationId } = useContext(PageContext);
  const { loading } = useContext(LoadingContext);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string>();
  const asyncVerifyPassword = useApi(verifyPassword);
  const handleError = useErrorHandler();

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'session.invalid_credentials': () => {
        setPasswordError(t('account_center.password_verification.error_failed'));
      },
      global: (error) => {
        setPasswordError(error.message);
      },
    }),
    [t]
  );

  const handleVerify = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setPasswordError(undefined);

    if (!password) {
      setPasswordError(t('error.password_required'));
      return;
    }

    const [error, result] = await asyncVerifyPassword(password);

    if (error) {
      await handleError(error, errorHandlers);
      return;
    }

    if (!result) {
      setPasswordError(t('account_center.password_verification.error_failed'));
      return;
    }

    setVerificationId(result.verificationRecordId, result.expiresAt);
  };

  return (
    <SecondaryPageLayout
      title="account_center.password_verification.title"
      description="account_center.password_verification.description"
      onBack={onBack}
    >
      <form noValidate className={styles.form} onSubmit={handleVerify}>
        <PasswordIdentifierInput />
        <PasswordInputField
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          id="current-password"
          name="currentPassword"
          autoComplete="current-password"
          label={t('input.password')}
          value={password}
          errorMessage={passwordError}
          isDanger={Boolean(passwordError)}
          onChange={(event) => {
            if (event.target instanceof HTMLInputElement) {
              setPassword(String(event.target.value));
            }
          }}
        />
        <Button
          className={styles.submit}
          htmlType="submit"
          title="action.continue"
          isLoading={loading}
        />
        {hasAlternativeMethod && (
          <SwitchVerificationMethodLink className={styles.switchLink} onClick={onSwitchMethod} />
        )}
      </form>
    </SecondaryPageLayout>
  );
};

export default PasswordVerification;
